import { useMemo } from 'react'
import type { CatalogProduct } from '../../types'
import {
  buildSearchFacets,
  computeProductPrice,
  countActiveSearchFilters,
  extractProductBrands,
  filterSearchProducts,
} from './productFilters'
import type { NumericFilterState, OptionFilterState, SearchFacets } from './types'

type Params = {
  products: CatalogProduct[]
  selectedBrands: string[]
  selectedTypes: string[]
  priceRange: { min: string; max: string }
  numericFilters: NumericFilterState
  optionFilters: OptionFilterState
  facets: SearchFacets | null
}

export const useSearchResultsDerivedData = ({
  products,
  selectedBrands,
  selectedTypes,
  priceRange,
  numericFilters,
  optionFilters,
  facets,
}: Params) => {
  const clientPriceBounds = useMemo(() => {
    const values = products
      .map((product) => computeProductPrice(product))
      .filter((value): value is number => value !== null)
    if (!values.length) return null
    return {
      min: 0,
      max: Math.max(...values),
    }
  }, [products])

  const clientProductTypeValues = useMemo(
    () =>
      Array.from(
        new Set(
          products
            .map((product) => (typeof product.productType === 'string' ? product.productType.trim() : ''))
            .filter(Boolean),
        ),
      ).sort((left, right) => left.localeCompare(right, 'it', { sensitivity: 'base' })),
    [products],
  )

  const clientBrandValues = useMemo(
    () =>
      Array.from(new Set(products.flatMap((product) => extractProductBrands(product)))).sort((left, right) =>
        left.localeCompare(right, 'it', { sensitivity: 'base' }),
      ),
    [products],
  )

  const clientFacets = useMemo(() => buildSearchFacets(products), [products])
  const priceBounds = facets?.priceBounds ?? clientPriceBounds
  const productTypeValues = facets?.productTypeValues ?? clientProductTypeValues
  const brandValues = facets?.brandValues ?? clientBrandValues
  const optionFacets = facets?.optionFacets ?? clientFacets.optionFacets
  const numericFacets = facets?.numericFacets ?? clientFacets.numericFacets

  const activeFilterCount = useMemo(
    () =>
      countActiveSearchFilters({
        selectedBrands,
        selectedTypes,
        priceRange,
        numericFilters,
        optionFilters,
      }),
    [numericFilters, optionFilters, priceRange, selectedBrands, selectedTypes],
  )

  const filteredProducts = useMemo(
    () =>
      facets ? products : filterSearchProducts({
        products,
        selectedBrands,
        selectedTypes,
        priceRange,
        numericFacets,
        numericFilters,
        optionFacets,
        optionFilters,
      }),
    [
      facets,
      numericFacets,
      numericFilters,
      optionFacets,
      optionFilters,
      priceRange,
      products,
      selectedBrands,
      selectedTypes,
    ],
  )

  return {
    priceBounds,
    productTypeValues,
    brandValues,
    optionFacets,
    numericFacets,
    activeFilterCount,
    filteredProducts,
  }
}
